<template>
  <div style="width: 100%;" :key="lang">

    <div class="section section-main-support align-items-center">
      <h1 class="mb-4 title" v-html="$t('SUPPORT_TITLE')"></h1>
      <p class="subtitle" v-html="$t('SUPPORT_SUBTITLE').replace(/{domain}/g, domain.title)"></p>
    </div>

    <div v-if="show" class="support-content base-padding">

      <!-- Download section -->
      <div class="section-download d-flex flex-column align-items-center">
        <h2 v-html="$t('SUPPORT_DOWNLOAD_TITLE')"></h2>
        <p v-html="$t('SUPPORT_DOWNLOAD_SUBTITLE').replace(/{domain}/g, domain.title)"></p>

        <div class="store-badges d-flex flex-row justify-content-center align-items-center">
          <a
            v-if="domain.app_store_url"
            :href="domain.app_store_url"
            target="_blank"
            rel="noopener noreferrer"
            class="store-badge-link"
            :title="$t('SUPPORT_APP_STORE')"
          >
            <img src="/assets/images/support/app-store-badge.svg" :alt="$t('SUPPORT_APP_STORE')" class="store-badge" />
          </a>

          <a
            v-if="domain.google_play_url"
            :href="domain.google_play_url"
            target="_blank"
            rel="noopener noreferrer"
            class="store-badge-link"
            :title="$t('SUPPORT_GOOGLE_PLAY')"
          >
            <img src="/assets/images/support/google-play-badge.svg" :alt="$t('SUPPORT_GOOGLE_PLAY')" class="store-badge" />
          </a>
        </div>
      </div>

      <!-- Contact section -->
      <div class="section-contact d-flex flex-column align-items-center">
        <h2 v-html="$t('SUPPORT_CONTACT_TITLE')"></h2>
        <p v-html="$t('SUPPORT_CONTACT_SUBTITLE')"></p>

        <div class="contact-options d-flex flex-row flex-wrap justify-content-center">
          <a
            v-if="domain.email2"
            :href="'mailto:' + domain.email2"
            class="contact-card"
          >
            <i class="fas fa-envelope"></i>
            <span>{{ domain.email2 }}</span>
          </a>

          <a
            v-if="domain.phone1"
            :href="'tel:' + domain.phone1"
            class="contact-card"
          >
            <i class="fas fa-phone-alt"></i>
            <span>{{ domain.phone1 }}</span>
          </a>
        </div>
      </div>

      <!-- FAQ section -->
      <div class="section-faq d-flex flex-column align-items-center">
        <h2 v-html="$t('SUPPORT_FAQ_TITLE')"></h2>

        <div class="faq-list">
          <div
            v-for="(item, index) in faqItems"
            :key="index"
            class="faq-item"
          >
            <div
              class="faq-question d-flex flex-row justify-content-between align-items-center"
              @click="toggleFaq(index)"
            >
              <span v-html="$t(item.question)"></span>
              <i :class="['fas', openFaq === index ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
            </div>
            <div class="faq-answer" v-if="openFaq === index">
              <p v-html="$t(item.answer).replace(/{domain}/g, domain.title)"></p>
            </div>
          </div>
        </div>
      </div>

    </div>

  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  props: {
    partner_url: {
      type: String,
      default: ''
    },
    window: {
      type: Object,
      default() {
        return {
          width: 0,
          height: 0
        }
      }
    }
  },

  computed: {
    ...mapState('MainStoreModule', {
      domain: state => state.domain
    }),

    locale() {
      return this.$i18n.locale
    }
  },

  data() {
    return {
      lang: 'en',
      show: false,
      openFaq: null,
      faqItems: [
        { question: 'SUPPORT_FAQ_Q1', answer: 'SUPPORT_FAQ_A1' },
        { question: 'SUPPORT_FAQ_Q2', answer: 'SUPPORT_FAQ_A2' },
        { question: 'SUPPORT_FAQ_Q3', answer: 'SUPPORT_FAQ_A3' },
        { question: 'SUPPORT_FAQ_Q4', answer: 'SUPPORT_FAQ_A4' },
        { question: 'SUPPORT_FAQ_Q5', answer: 'SUPPORT_FAQ_A5' },
      ]
    }
  },

  watch: {
    locale(locale) {
      this.lang = locale == 'pt_br' ? 'pt' : locale
    }
  },

  beforeCreate() {
    this.$emit('isPageLoaded', false)
  },

  mounted() {
    const params = this.$route.params
    if (Object.keys(params).length && params.lang) {
      this.lang = params.lang
      this.changeLocale(this.lang)
    }

    setTimeout(() => {
      this.$emit('isPageLoaded', true)
      this.show = true
    }, 500)
  },

  methods: {
    ...mapActions('MainStoreModule', ['setLocale']),

    changeLocale(locale) {
      this.$i18n.locale = locale
      this.setLocale(locale)
      this.$moment.locale(locale)
    },

    toggleFaq(index) {
      this.openFaq = this.openFaq === index ? null : index
    }
  }
}
</script>

<style scoped lang="scss">

.section-main-support {
  min-height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: linear-gradient(135deg, #0878ce 0%, #2C2FFA 100%);
  padding: 60px 10vw;

  h1.title {
    color: #fff;
    font-size: 42px;
    font-weight: 700;
    margin: 0;
  }

  .subtitle {
    color: rgba(255, 255, 255, 0.85);
    font-size: 18px;
    margin: 0;
  }
}

.support-content {
  padding-bottom: 80px;
}

.section-download {
  padding: 60px 0 40px;
  text-align: center;

  h2 {
    font-size: 28px;
    font-weight: 700;
    color: #282828;
    margin-bottom: 12px;
  }

  p {
    font-size: 16px;
    color: #666;
    margin-bottom: 32px;
  }
}

.store-badges {
  gap: 24px;
  flex-wrap: wrap;
}

.store-badge-link {
  display: inline-block;
  transition: transform 0.2s ease, opacity 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    opacity: 0.9;
  }
}

.store-badge {
  height: 52px;
  width: auto;
}

.section-contact {
  padding: 40px 0;
  text-align: center;
  background: #f8f9fa;
  border-radius: 16px;
  margin: 0 -4vw;
  padding: 48px 4vw;

  h2 {
    font-size: 28px;
    font-weight: 700;
    color: #282828;
    margin-bottom: 12px;
  }

  p {
    font-size: 16px;
    color: #666;
    margin-bottom: 32px;
  }
}

.contact-options {
  gap: 24px;
}

.contact-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 28px 36px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  color: #0878ce;
  text-decoration: none;
  min-width: 200px;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 8px 28px rgba(8, 120, 206, 0.18);
    transform: translateY(-2px);
    color: #0878ce;
    text-decoration: none;
  }

  i {
    font-size: 28px;
  }

  span {
    font-size: 15px;
    font-weight: 500;
  }
}

.section-faq {
  padding: 60px 0 0;
  text-align: center;

  h2 {
    font-size: 28px;
    font-weight: 700;
    color: #282828;
    margin-bottom: 40px;
  }
}

.faq-list {
  width: 100%;
  max-width: 720px;
  text-align: left;
}

.faq-item {
  border-bottom: 1px solid #e8e6e6;
  padding: 4px 0;
}

.faq-question {
  padding: 18px 0;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  color: #282828;
  gap: 16px;

  i {
    color: #0878ce;
    font-size: 14px;
    flex-shrink: 0;
  }

  &:hover {
    color: #0878ce;
  }
}

.faq-answer {
  padding: 0 0 18px;

  p {
    margin: 0;
    font-size: 15px;
    color: #555;
    line-height: 1.7;
  }
}

@media only screen and (max-width: 991px) {
  .section-main-support {
    min-height: 200px;
    padding: 40px 6vw;

    h1.title {
      font-size: 26px;
    }

    .subtitle {
      font-size: 15px;
    }
  }

  .store-badges {
    flex-direction: column;
    align-items: center;
  }

  .contact-options {
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  .contact-card {
    width: 100%;
    max-width: 320px;
    flex-direction: row;
    justify-content: center;
  }
}
</style>
